import classNames from 'classnames'

import { ConversationTransport } from '~/api/graphql/generated/types'
import EmailPreview from '~/components/common/emailPreview/EmailPreview'
import { MessageVariant, MessageWay } from '~/constants/communications'
import type { ChatMessage } from '~/types'
import {
  getMessageTransportName,
  isInternalNote,
  isPlainTextTransport,
} from '~/utils/communicationsUtils'

import MessageActions from './MessageActions'
import MessageHeader from './MessageHeader'
import MessageStatusTag from './MessageStatusTag'
import MessageTypeIcon from './MessageTypeIcon'

import styles from './MessageCardV2.module.scss'

export interface MessageCardV2Props {
  message: ChatMessage
  readOnly?: boolean
  variant?: MessageVariant
}
const MessageCardV2 = ({
  message,
  readOnly = false,
  variant = MessageVariant.PRIMARY,
}: MessageCardV2Props) => {
  const { content, type, isMedical, way, transport } = message || {}
  const isMiniMode = variant === MessageVariant.MINI
  const isInternalNoteMessage = isInternalNote(transport, type)
  const isPlainText = isPlainTextTransport(transport) && !isInternalNoteMessage
  const isInboundEmail =
    transport === ConversationTransport.Email && way === MessageWay.INBOUND
  const messageTransport = isInternalNoteMessage ? null : transport
  const iconLabel = getMessageTransportName(messageTransport)

  return (
    <div className={styles.message}>
      <MessageHeader isReadOnly={readOnly} message={message} />
      <div
        className={classNames(styles.messageCard, {
          [styles.internalNoteMessage]: isInternalNoteMessage,
          [styles.medicalMessage]: isMedical,
          [styles.adminMessage]: !isMedical && !isInternalNoteMessage,
        })}
      >
        <div className={styles.messageTypeLabel}>
          <div className={styles.messageTypeIcon}>
            <MessageTypeIcon messageTransport={messageTransport} />
          </div>
          <div className={styles.messageTypeText}>{iconLabel}</div>
          <MessageStatusTag
            isInternalNote={isInternalNoteMessage}
            status={message.status}
          />
        </div>
        {content && (
          <EmailPreview
            allowPointerEvents
            classes={{
              previewContainerWrapper: classNames({
                [styles.previewContainerWrapper]: !isInboundEmail,
                [styles.previewContainerWrapperWithBackground]: isInboundEmail,
              }),
              plainTextPreview: classNames({
                [styles.primaryPlainTextPreview]: !isMiniMode,
              }),
            }}
            disableHtmlRendering={isPlainText}
            emailPreviewShadowDomClasses={{
              previewContainer: classNames({
                [styles.miniShadowDomPreviewContainer]: isMiniMode,
                [styles.primaryShadowDomPreviewContainer]: !isMiniMode,
              }),
            }}
            emailTemplate={content}
            scale={1}
          />
        )}
      </div>
      <MessageActions message={message} readOnly={readOnly} />
    </div>
  )
}

export default MessageCardV2
